/**
 * @fileoverview The badge TONE vocabulary and its one resolver (Q-MI-25).
 *
 * `.badge[data-tone]` (shared-styles `badges.css`) is the ecosystem's one tone pill, emitted by
 * one primitive, `<StatusBadgeCell>` (`@puffin-core/page-shell`). Its tone set is CLOSED —
 * success · warning · danger · info · neutral — and every tone renders SOFT (a tinted fill with
 * dark text; the hue carries the meaning, the visual weight is uniform).
 *
 * The vocabulary lives here, beside the status SSOT, because the question it answers — "which
 * tone does this STATUS read as?" — is a status question. A domain declares a frozen
 * `status → tone` map next to its status enum; `resolveBadgeTone` is the only place that map is
 * read, and it refuses any value outside the closed set. A tone the CSS does not define would
 * render the unstyled base with no error anywhere — the failure class
 * `web/no-constructed-status-class` exists to prevent at the call site — so it is refused here,
 * loudly, at the one seam every tone passes through.
 *
 * DOMAIN statuses whose vocabulary is OPEN (SIP registration, porting milestones) keep
 * `.status-pill[data-status]`; they are a distinct need and never pass through this resolver.
 *
 * @module @puffin-core/status-taxonomy/badgeTones
 */

'use strict';

/** The closed tone vocabulary of `.badge[data-tone]`. */
const BADGE_TONE = Object.freeze({
  SUCCESS: 'success',
  WARNING: 'warning',
  DANGER: 'danger',
  INFO: 'info',
  NEUTRAL: 'neutral',
});

const BADGE_TONE_VALUES = Object.freeze(Object.values(BADGE_TONE));

/**
 * @param {unknown} value
 * @returns {boolean} true when `value` is one of the five tones.
 */
function isBadgeTone(value) {
  return typeof value === 'string' && BADGE_TONE_VALUES.includes(value);
}

function assertTone(value, source) {
  if (!isBadgeTone(value)) {
    throw new TypeError(
      `badge tone "${value}" (${source}) is not one of ${BADGE_TONE_VALUES.join(', ')}`,
    );
  }
  return value;
}

/**
 * Resolve the tone a badge renders.
 *
 * Precedence: an explicit `tone` → the caller's `statusTones[status]` (own keys only) →
 * `defaultTone`. A status the map does not list is legitimate (a value the domain has not
 * given a tone) and takes the default; a VALUE outside the closed set is a defect and throws.
 *
 * @param {Object} [options]
 * @param {string} [options.status] - The raw domain status.
 * @param {string|null} [options.tone] - An explicit tone, when the caller computes it (null = absent).
 * @param {Object<string,string>} [options.statusTones] - Frozen status → tone map.
 * @param {string} [options.defaultTone='neutral'] - Tone for a status the map does not list.
 * @returns {string} one of `BADGE_TONE_VALUES`.
 * @throws {TypeError} when any supplied tone is outside the closed set.
 */
function resolveBadgeTone({ status, tone, statusTones, defaultTone = BADGE_TONE.NEUTRAL } = {}) {
  if (tone !== undefined && tone !== null) return assertTone(tone, 'explicit tone');
  if (statusTones && typeof status === 'string' && Object.hasOwn(statusTones, status)) {
    return assertTone(statusTones[status], `statusTones["${status}"]`);
  }
  return assertTone(defaultTone, 'defaultTone');
}

module.exports = {
  BADGE_TONE,
  BADGE_TONE_VALUES,
  isBadgeTone,
  resolveBadgeTone,
};
